import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Badge, Container, Button, Modal, Image, Spinner } from "react-bootstrap";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import Swal from "sweetalert2";
import { FiDownload, FiZoomIn, FiArrowLeft, FiTrash2, FiEdit2 } from "react-icons/fi";

const ProductView = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${BASE_URL}product/findById/${id}`, {
          headers: authHeader(),
        });
        setProduct(response.data);
        if (response.data.productImages?.length > 0) {
          setFiles(response.data.productImages);
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch product data.",
          confirmButtonColor: "#0d6efd"
        });
        navigate("/productindex/1");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const getProductTypeBadge = (producttype) => {
    switch (producttype) {
      case "Finished": return 'success';
      case "Raw Material": return 'warning';
      case "Semi Finished": return 'info';
      default: return 'secondary';
    }
  };

  const fetchAndOpenFile = async (filename) => {
    try {
      const response = await axios.get(`${BASE_URL}productImages/findImageByFilename/${filename}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      setFileType(contentType.startsWith("image") ? "image" : "pdf");
      setFileUrl(url);
      setModalShow(true);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to open file.",
        confirmButtonColor: "#0d6efd"
      });
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await axios.get(`${BASE_URL}productImages/findImageByFilename/${filename}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Could not download file.',
        confirmButtonColor: "#0d6efd"
      });
    }
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setFileUrl(null);
  };

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "product/delete/" + id, {
          headers: authHeader(),
        }).then(response => {
          if (response.data.length === 0) {
            Swal.fire('Warning', 'Product present in another module. Delete operation has been cancelled.', 'warning');
          } else {
            navigate(`/productindex/${pointindex}`);
            Swal.fire('Deleted!', 'Deleted successfully.', 'success');
          }
        }).catch((error) => {
          Swal.fire('Error', error.response.data, 'error');
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!product) {
    return <div className="alert alert-danger text-center mt-4">Product not found</div>;
  }

  const { productcategory, producttype } = product;
  const isPumpSpare = productcategory === "Pump Spare";
  const isManufacturing = ["International Manufacturing", "Domestic Manufacturing"].includes(productcategory);
  const isFinished = producttype === "Finished";
  const isRawMaterial = producttype === "Raw Material";
  const isSemiFinished = producttype === "Semi Finished";

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">PRODUCT - VIEW</h5>
            <div>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => getItem(id)}>
                <FiTrash2 className="me-1" />
                Delete
              </Button>
              <Link to={`/productEdit/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FiEdit2 className="me-1" />
                Edit
              </Link>
              <Link to="/productindex/1" className="btn btn-secondary btn-sm">
                <FiArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Product ID</small>
                <div className="fw-bold">#{id}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Category</small>
                <div>
                  <Badge bg="primary" className="px-2 py-1">{product.productcategory || '-'}</Badge>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Type</small>
                <div>
                  <Badge bg={getProductTypeBadge(product.producttype)} className="px-2 py-1">
                    {product.producttype || '-'}
                  </Badge>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Brand</small>
                <div className="fw-bold">{product.brand?.brandname || '-'}</div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Basic Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Basic Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Brand</small>
              <div className="fw-bold">{product.brand?.brandname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Stock Group</small>
              <div className="fw-bold">{product.productGroup?.groupname || '-'}</div>
            </Col>
            <Col md={12}>
              <small className="text-muted">Product Description</small>
              <div className="fw-bold">{product.description || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Product Specific Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Product Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            {/* Customer Name - for Pump Spare & Manufacturing Finished */}
            {(isPumpSpare || (isManufacturing && isFinished)) && (
              <Col md={4}>
                <small className="text-muted">Customer Name</small>
                <div className="fw-bold">
                  {product.customer ? `${product.customer.companyname} - ${product.customer.contactperson}` : '-'}
                </div>
              </Col>
            )}
            <Col md={4}>
              <small className="text-muted">HSN Code</small>
              <div className="fw-bold">{product.hsnsacno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Unit Of Measurement</small>
              <div className="fw-bold">{product.unitofmeasurement || '-'}</div>
            </Col>

            {/* Model & Tag - for Pump Spare & Manufacturing Finished */}
            {(isPumpSpare || (isManufacturing && isFinished)) && (
              <>
                <Col md={4}>
                  <small className="text-muted">Model Number</small>
                  <div className="fw-bold">{product.modelnumber || '-'}</div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Tag Number</small>
                  <div className="fw-bold">{product.tagnumber || '-'}</div>
                </Col>
              </>
            )}

            {/* Serial Number - for Pump Spare & Manufacturing Finished */}
            {(isPumpSpare || (isManufacturing && isFinished)) && (
              <Col md={4}>
                <small className="text-muted">Serial Number</small>
                <div className="fw-bold">{product.serialnumber || '-'}</div>
              </Col>
            )}

            {/* Item Code - for Pump Spare & Manufacturing Finished/Semi Finished */}
            {(isPumpSpare || (isManufacturing && (isFinished || isSemiFinished))) && (
              <Col md={4}>
                <small className="text-muted">Item Code</small>
                <div className="fw-bold">{product.itemcode || '-'}</div>
              </Col>
            )}

            {/* MOC */}
            {(isPumpSpare || isManufacturing) && (
              <Col md={4}>
                <small className="text-muted">MOC</small>
                <div className="fw-bold">{product.moc || '-'}</div>
              </Col>
            )}

            {/* Part No */}
            {(isPumpSpare || isManufacturing) && (
              <Col md={4}>
                <small className="text-muted">Part No</small>
                <div className="fw-bold">{product.partno || '-'}</div>
              </Col>
            )}

            {/* Size - for Manufacturing Raw/Semi */}
            {(isManufacturing && (isRawMaterial || isSemiFinished)) && (
              <Col md={4}>
                <small className="text-muted">Size</small>
                <div className="fw-bold">{product.size || '-'}</div>
              </Col>
            )}

            {/* Schedule & Class - for Manufacturing Raw/Semi */}
            {(isManufacturing && (isRawMaterial || isSemiFinished)) && (
              <>
                <Col md={4}>
                  <small className="text-muted">Schedule</small>
                  <div className="fw-bold">{product.schedule || '-'}</div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Class</small>
                  <div className="fw-bold">{product.classes || '-'}</div>
                </Col>
              </>
            )}

            {/* Rating & Type - for Semi Finished */}
            {isSemiFinished && (
              <>
                <Col md={4}>
                  <small className="text-muted">Rating</small>
                  <div className="fw-bold">{product.rating || '-'}</div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Type</small>
                  <div className="fw-bold">{product.type || '-'}</div>
                </Col>
              </>
            )}

            {/* Labour Charges - for Manufacturing Finished */}
            {(isManufacturing && isFinished) && (
              <Col md={4}>
                <small className="text-muted">Labour Charges (%)</small>
                <div className="fw-bold">{product.labourcharges || '-'}</div>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* Tax Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Tax Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">IGST</small>
              <div className="fw-bold">{product.igst || 0}%</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">CGST</small>
              <div className="fw-bold">{product.cgst || 0}%</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">SGST</small>
              <div className="fw-bold">{product.sgst || 0}%</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Product Images */}
      {files.length > 0 && (
        <div className="shadow-sm border rounded bg-white mb-3">
          <div className="bg-light py-2 px-3 border-bottom">
            <h6 className="mb-0 fw-bold">Product Images</h6>
          </div>
          <div className="p-3">
            <Row className="g-3">
              {files.map((file, i) => (
                <Col key={i} xs={6} sm={4} md={3} lg={2}>
                  <div className="border rounded-3 p-2 text-center shadow-sm bg-white">
                    <div 
                      style={{ cursor: 'pointer', position: 'relative' }}
                      onClick={() => fetchAndOpenFile(file.filename)}
                    >
                      <Image
                        src={`${BASE_URL}productImages/findImageByFilename/${file.filename}`}
                        alt={`Product ${i + 1}`}
                        thumbnail
                        className="w-100 mb-2"
                        style={{ height: '120px', objectFit: 'cover' }}
                      />
                      <div 
                        style={{
                          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                          background: 'rgba(0,0,0,0.3)', opacity: 0, transition: 'opacity 0.2s',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                      >
                        <FiZoomIn size={24} color="white" />
                      </div>
                    </div>
                    <small className="text-truncate d-block mb-2">{file.filename}</small>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100"
                      onClick={() => downloadFile(file.filename)}
                    >
                      <FiDownload size={12} className="me-1" /> Download
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/productEdit/${id}/${pointindex}`} className="btn btn-primary btn-sm me-2">
          <FiEdit2 className="me-1" />
          Edit Product
        </Link>
        <Link to="/productindex/1" className="btn btn-secondary btn-sm">
          <FiArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>

      {/* File Preview Modal */}
      <Modal show={modalShow} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 bg-light">
          {fileUrl && (fileType === "pdf" ? (
            <iframe
              title="PDF Viewer"
              src={fileUrl}
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          ) : (
            <Image src={fileUrl} alt="Preview" fluid />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" size="sm" onClick={() => downloadFile(fileUrl?.split('/').pop())}>
            <FiDownload className="me-2" /> Download
          </Button>
          <Button variant="secondary" size="sm" onClick={handleCloseModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductView;